/* חיסכון.
   העברה מהעו"ש לפיקדון, לחיסכון או לקופת גמל היא לא הוצאה: הכסף רק עבר מקום.
   לכן היא מוחרגת מסיכומי ההוצאות (אבל ממשיכה להוריד מיתרת העו"ש, כי שם היא באמת יצאה),
   ונספרת ביעד החיסכון שהמילים שלו מופיעות בתיאור שלה. */
import { S } from './state.js';
import { mNorm } from '../lib/text.js';
import { todayISO, iso10, absAmt } from '../lib/format.js';
import { getTxSrc } from './tx.js';
import { curKey } from './periods.js';

export const SAV_RX = /פיקדון|פקדון|חסכון|חיסכון|קופת גמל|קופ"ג|גמל להשקעה/i;

export function savCfg(id) {
  return (S.set.savCfg || {})[id] || {};
}
export function savKws(id) {
  return String(savCfg(id).kw || '').split(',').map((x) => mNorm(x).trim()).filter((x) => x.length >= 2);
}
export function savFrom(g) {
  return savCfg(g.id).from || iso10(g.created_at) || '2000-01-01';
}

/* היעד שהעסקה שייכת אליו. נקרא אלפי פעמים בכל ציור (דרך isExcluded), ולכן נשמר עד שהנתונים משתנים */
let cache = { ver: -1, map: new WeakMap() };
export function savGoalOf(t) {
  if (!t || getTxSrc(t) === 'credit' || (t.type !== 'expense' && t.type !== 'income')) return null;
  if (!S.savings.length) return null;
  if (cache.ver !== S.dataVer) cache = { ver: S.dataVer, map: new WeakMap() };
  if (cache.map.has(t)) return cache.map.get(t);
  const res = savGoalOf0(t);
  cache.map.set(t, res);
  return res;
}
function savGoalOf0(t) {
  if (t.type === 'income' && /ריבית/.test(t.description || '')) return null; // ריבית היא הכנסה, לא משיכה
  const n = mNorm(t.description || ''), ds = iso10(t.date);
  for (const g of S.savings) {
    const kw = savKws(g.id);
    if (!kw.length || ds < savFrom(g)) continue;
    for (const k of kw) if (n.indexOf(k) >= 0) return g.id;
  }
  return null;
}

/** העברה לחיסכון או משיכה ממנו. ריבית על פיקדון היא הכנסה אמיתית, ולכן לא נספרת */
export function isSavingTx(t) {
  if (!t || typeof t !== 'object' || getTxSrc(t) === 'credit') return false;
  if (t.type !== 'expense' && t.type !== 'income') return false;
  if (t.type === 'income' && /ריבית/.test(t.description || '')) return false;
  return SAV_RX.test(t.description || '') || savGoalOf(t) != null;
}
export function isSavingRec(r) {
  if (!r || r.type === 'income') return false;
  if (SAV_RX.test(r.description || '')) return true;
  const n = mNorm(r.description || '');
  return S.savings.some((g) => savKws(g.id).some((k) => n.indexOf(k) >= 0));
}

export function savMonthsTo(iso) {
  const t0 = new Date(todayISO() + 'T12:00:00');
  return Math.max(0.5, (new Date(iso + 'T12:00:00') - t0) / 86400000 / 30.44);
}

/** מצב יעד אחד: כמה יש בו, כמה חסר, וכמה צריך בחודש */
export function savGoalStats(g) {
  const base = Number(g.saved) || 0, k = curKey(), list = [];
  let dep = 0, wd = 0, per = 0;
  S.tx.forEach((t) => {
    if (savGoalOf(t) !== g.id) return;
    const a = absAmt(t);
    if (t.type === 'expense') { dep += a; if (t.month === k) per += a; }
    else { wd += a; if (t.month === k) per -= a; }
    list.push(t);
  });
  const total = base + dep - wd, target = Number(g.target) || 0, rem = Math.max(0, target - total);
  const cfg = savCfg(g.id);
  let monthly = 0, src = '';
  // היעד החודשי נמדד מתחילת התקופה, כדי שהפקדה של החודש לא תקטין את היעד של אותו חודש
  const remStart = Math.max(0, target - (total - per));
  if (g.deadline && remStart > 0) { monthly = remStart / savMonthsTo(iso10(g.deadline)); src = 'deadline'; }
  else if (cfg.monthly > 0 && remStart > 0) { monthly = Math.min(remStart, Number(cfg.monthly)); src = 'fixed'; }
  // קצב בפועל: ממוצע ההפקדות נטו בשלוש התקופות האחרונות שבהן היו הפקדות
  const byM = {};
  list.forEach((t) => {
    const a = absAmt(t);
    byM[t.month] = (byM[t.month] || 0) + (t.type === 'expense' ? a : -a);
  });
  const ms = Object.keys(byM).sort().slice(-3);
  const avg = ms.length ? ms.reduce((s, m) => s + byM[m], 0) / ms.length : 0;
  let eta = null;
  if (rem > 0) {
    const pace = monthly || avg;
    if (pace > 0) { eta = new Date(); eta.setDate(eta.getDate() + Math.round((rem / pace) * 30.44)); }
  }
  return {
    g, base, dep, wd, total, target, rem, remStart, pct: target ? Math.min(100, (total / target) * 100) : 0,
    monthly: Math.round(monthly), monthlySrc: src, per, avg: Math.round(avg), eta, list, kw: savKws(g.id),
    done: target > 0 && rem <= 0,
  };
}

/** העברות לחיסכון שלא שויכו לאף יעד */
export function savUnassigned() {
  return S.tx.filter((t) => isSavingTx(t) && savGoalOf(t) == null).sort((a, b) => String(b.date).localeCompare(String(a.date)));
}
